import React from "react";
import {
  Page,
  Text,
  Link,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import Html from "react-pdf-html";
import Helper from "../functions/Helper.js";
import Play from "./Play.jsx";

/*
import NotoSansJPRegular from "../../fonts/Noto_Sans_JP/static/NotoSansJP-Regular.ttf";
import NotoSansJPBold from "../../fonts/Noto_Sans_JP/static/NotoSansJP-Bold.ttf";

import NotoSansKRRegular from "../../fonts/Noto_Sans_KR/static/NotoSansKR-Regular.ttf";
import NotoSansKRBold from "../../fonts/Noto_Sans_KR/static/NotoSansKR-Bold.ttf"; */

import { pdfFont } from "./utils.js";

const stylesheet = (fontSize) => ({
  // clear margins for all <p> tags
  "*": {
    fontFamily: pdfFont,
    fontSize: fontSize,
  },
  "p,ol,ul,pre,blockquote,h1,h2,h3,h4,h5,h6": {
    margin: 0,
    padding: 0,
  },
  ".ql-indent-1": {
    paddingLeft: fontSize * 1,
  },
  ".ql-indent-2": {
    paddingLeft: fontSize * 2,
  },
  ".ql-indent-3": {
    paddingLeft: fontSize * 3,
  },
  ".ql-indent-4": {
    paddingLeft: fontSize * 4,
  },
  ".ql-indent-5": {
    paddingLeft: fontSize * 5,
  },
  ".ql-indent-6": {
    paddingLeft: fontSize * 6,
  },
  ".ql-indent-7": {
    paddingLeft: fontSize * 7,
  },
  "ol,ul": {
    paddingLeft: 0,
    margin: 0,
  },
  li: {
    paddingBottom: 5,
  },
  ul: {
    marginLeft: -10,
  },
});

const getPreviewCutoff = (chapters, amountOfPlays = 7) => {
  if (chapters.length === 0) return [0, 0];
  try {
    let counter = 0;
    for (let i = 0; i < chapters.length; i++) {
      for (let j = 0; j < chapters[i]?.objects?.length; j++) {
        if (chapters[i]?.objects[j]?.type === "play") {
          counter++;
        }
        if (counter >= amountOfPlays) {
          return [i, j];
        }
      }
    }
    return [chapters.length, chapters[chapters.length - 1].objects.length - 1];
  } catch (e) {
    console.log("getPreviewCutoff error", e);
    return [0, 0];
  }
};

export default function PlaybookPDF({
  saveToDbRights,
  showFullPlaybook = true,
  downloadPDF = false,
  dbData,
  chapters,
  coverSettings,
  printSettings,
  tocData = [],
}) {
  let {
    cols,
    courtColor,
    fontSizes,
    pageBreakAfterPlay,
    pagePadding,
    printPlayDescriptions,
    printPhaseDescriptions,
    printPhaseTitles,
    objectSizeFactor,
    overwriteScaling,
  } = printSettings;

  let {
    showCoverPage,
    showCoverImage,
    image,
    imageWidth,
    imageHeight,
    subtitle,
    titleFontSize,
    subtitleFontSize,
    titleMarginTop,
    subtitleMarginTop,
    verticalAlignment,
  } = coverSettings ?? Helper.defaultCoverSettings;

  let pageWidth = 595;
  let paddingWidth = (pagePadding.horizontal / 8.3) * pageWidth;
  let framePadding = 10;
  let frameWidth =
    (pageWidth - 2 * paddingWidth + framePadding) / cols - framePadding;
  let styles = stylesGenerator(pagePadding);

  //iterate through the chapters and add up to amount of objects object.type === "play" and get the first chapter where we have at least 5 objects
  //iterate through the objects and add up to amount of objects object.type === "play" and get the first chapter where we have at least 5 objects

  let previewCutoff = getPreviewCutoff(chapters);

  return (
    <Document>
      {showCoverPage && (
        <Page
          key={"cover"}
          size="A4"
          style={{
            ...styles.page,
            justifyContent: verticalAlignment,
            textAlign: "center",
          }}
        >
          {showCoverImage &&
            !(downloadPDF && !image) &&
            !(!saveToDbRights && !image) && (
              <View
                style={{
                  width: "100%",
                  height: (imageHeight + "%" ?? "30") + "%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!image && !downloadPDF && saveToDbRights && (
                  <View
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Text style={{ color: "#666" }}>
                      Add or Remove Image in Tab "Cover Page"
                    </Text>
                  </View>
                )}

                {image && (
                  <Image
                    style={{
                      objectFit: "contain",
                      width: (imageWidth + "%" ?? "100") + "%",
                    }}
                    src={image + "?pdf=true"}
                  />
                )}
              </View>
            )}

          <Text
            style={{
              marginTop: titleMarginTop,
              fontSize: titleFontSize,
              fontWeight: "bold",
              fontFamily: pdfFont,
            }}
          >
            {dbData.title}
          </Text>

          <Text
            style={{
              marginTop: subtitleMarginTop,
              fontSize: subtitleFontSize,
              fontFamily: pdfFont,
            }}
          >
            {subtitle}
          </Text>
        </Page>
      )}
      <Page key={0} size="A4" style={styles.page}>
        {!showCoverPage && (
          <View>
            <Text
              style={{
                fontSize: fontSizes.title,
                fontWeight: "bold",
                fontFamily: pdfFont,
              }}
            >
              {dbData.title}
            </Text>
          </View>
        )}
        <View>
          <Text
            style={{
              fontSize: 18,
              fontWeight: "bold",
              marginTop: 30,
              marginBottom: 0,
              fontFamily: pdfFont,
            }}
          >
            Table of Contents
          </Text>
          {chapters.map((chapter, chapterIndex) => (
            <View key={chapterIndex} style={{ marginTop: 15 }}>
              <Link
                style={{ color: "black", textDecoration: "none" }}
                src={"#chapter" + chapterIndex}
              >
                <Text
                  style={{
                    fontFamily: pdfFont,
                    fontSize: 14,
                    marginBottom: 5,
                    fontWeight: "bold",
                  }}
                >
                  {chapterIndex + 1}.{" " + chapter.title}
                </Text>
              </Link>
              {chapter.objects.map((object, index) => {
                if (object.type === "pageBreak") return null;

                return (
                  <View
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      marginLeft: 20,
                      marginBottom: 5,
                    }}
                  >
                    <View style={{}}>
                      <Link
                        style={{ color: "black", textDecoration: "none" }}
                        src={"#chapter" + chapterIndex + "object" + index}
                      >
                        <Text style={{ fontFamily: pdfFont, fontSize: 10 }}>
                          {object.type === "play"
                            ? object.data?.title
                            : object.title}{" "}
                        </Text>
                      </Link>
                    </View>
                    <View
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <View
                        style={{
                          height: 1,
                          flexGrow: 1,
                          borderBottomWidth: 1,
                          borderBottomColor: "#999",
                          marginHorizontal: 10,
                          borderStyle: "dotted",
                        }}
                      ></View>
                    </View>
                    <View>
                      <Link
                        style={{ color: "black", textDecoration: "none" }}
                        src={"#chapter" + chapterIndex + "object" + index}
                      >
                        <Text style={{ fontSize: 10, fontFamily: pdfFont }}>
                          {tocData?.[chapterIndex + "-" + (index + 1)]?.page}
                        </Text>{" "}
                      </Link>
                    </View>
                  </View>
                );
              })}
            </View>
          ))}
        </View>
        {chapters.map((chapter, chapterIndex) => {
          if (!showFullPlaybook && chapterIndex > previewCutoff[0]) return null;

          return (
            <>
              <View key={chapterIndex} break />
              <View
                render={({ pageNumber }) => {
                  tocData[chapterIndex + "-0"] = {
                    title: chapter.title,
                    page: pageNumber,
                  };
                }}
              />
              <Text
                id={"chapter" + chapterIndex}
                bookmark={chapterIndex + 1 + "." + chapter.title}
                style={{
                  fontFamily: pdfFont,
                  marginBottom: 20,
                  fontSize: fontSizes.chapter,
                  fontWeight: "bold",
                }}
              >
                {chapterIndex + 1}.{" " + chapter.title}
              </Text>
              {chapter.objects.map((object, index) => {
                if (
                  !showFullPlaybook &&
                  chapterIndex === previewCutoff[0] &&
                  index > previewCutoff[1]
                )
                  return null;
                else if (object.type === "play" && !!object?.data?.data) {
                  return (
                    <View
                      style={{
                        paddingBottom: pageBreakAfterPlay ? 0 : 30,
                      }}
                      break={index !== 0 && pageBreakAfterPlay}
                      key={index}
                    >
                      <View
                        render={({ pageNumber }) => {
                          tocData[chapterIndex + "-" + (index + 1)] = {
                            type: object.type,
                            title:
                              object.type === "play" ? object.data.title : "",
                            page: pageNumber,
                          };
                        }}
                      />

                      <Play
                        id={"chapter" + chapterIndex + "object" + index}
                        cols={cols}
                        court={object.data.data.settings.editor.court}
                        courtColor={courtColor}
                        description={object.data.data.settings.description}
                        fontSizes={fontSizes}
                        framePadding={framePadding}
                        frameWidth={frameWidth}
                        //maxHeight={maxHeight}
                        objectSizeFactor={
                          overwriteScaling
                            ? objectSizeFactor
                            : object.data.data.settings?.objectSizeFactor ?? 1
                        }
                        pageWidth={pageWidth}
                        paddingWidth={paddingWidth}
                        phases={object.data.data.phases.phases}
                        printPhaseDescriptions={printPhaseDescriptions}
                        printPhaseTitles={printPhaseTitles}
                        printPlayDescriptions={printPlayDescriptions}
                        stylesheet={stylesheet}
                        title={object.data.title}
                      />
                    </View>
                  );
                } else if (object.type === "text")
                  return (
                    <View
                      style={{
                        paddingBottom: 30,
                      }}
                      break={
                        index !== 0 &&
                        pageBreakAfterPlay &&
                        object.type === "play"
                      }
                      key={index}
                    >
                      <View
                        render={({ pageNumber }) => {
                          tocData[chapterIndex + "-" + (index + 1)] = {
                            type: object.type,
                            title:
                              object.type === "play" ? object.data.title : "",
                            page: pageNumber,
                          };
                        }}
                      />

                      <View wrap={(object.data?.length ?? 0) > 500}>
                        <View style={{ marginBottom: 20 }}>
                          <Text
                            bookmark={object.title}
                            style={{
                              fontSize: fontSizes.objectTitle,
                              fontWeight: "bold",
                              fontFamily: pdfFont,
                            }}
                          >
                            {object.title}
                          </Text>
                        </View>
                        <Html
                          style={{
                            fontSize: fontSizes.play,
                            fontFamily: pdfFont,
                          }}
                          stylesheet={stylesheet(fontSizes.play)}
                        >
                          {object.data}
                        </Html>
                      </View>
                    </View>
                  );
                else if (object.type === "pageBreak")
                  return <View key={index} break />;
              })}
            </>
          );
        })}

        <Text
          style={{
            position: "absolute",
            fontSize: 8,
            bottom: 35,
            left: 0,
            right: 0,
            textAlign: "center",
            color: "grey",
          }}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}

const stylesGenerator = (pagePadding) =>
  StyleSheet.create({
    page: {
      fontSize: 12,
      fontFamily: "Public Sans",
      padding: `${pagePadding.vertical}in ${pagePadding.horizontal}in`,
      backgroundColor: "#ffffff",
      border: "1px solid #ccc",
    },
  });
